import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";

import Checkbox from "~/components/common/select/checkbox";
import { declOfNum, getWordLat, getWordSound } from "~/utils";

import styles from "./index.module.scss";

function isFiltered(filter: string, name: string, library: any) {
  if (!filter?.trim()) {
    return true;
  }

  const filterArray = filter.trim().toLowerCase().replace("-", " ").split(" ");
  filterArray.forEach((filterPiece) => {
    filterArray.push(getWordSound(filterPiece));
    filterArray.push(getWordLat(filterPiece));
  });

  filterArray.forEach((filterPiece) => {
    library.forEach((librarySet: any) => {
      librarySet.forEach((libraryItem: any) => {
        if (libraryItem.includes(filterPiece)) {
          filterArray.push(...librarySet);
        }
      });
    });
  });

  let isFound = false;
  filterArray.forEach((filterPiece) => {
    if (
      name.toLowerCase().includes(filterPiece) ||
      name.toLowerCase().replace("-", "").includes(filterPiece)
    ) {
      isFound = true;
    }
  });

  return isFound;
}

export function Select({
  options = [],
  selected = [],
  isDisabled = false,
  placeholder = "Выберите элементы",
  onChange,
  trigger,
  onClickDisabled = () => {},
  searchTranslateLibrary = [],
  onOpened = () => {},
  type = "normal",
  /** Префикс data-test: `${test}Box` на кнопке селекта, `${test}Option` на пунктах. */
  test = "",
}: any) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    if (search) {
      return options.filter((item: any) => {
        return (
          typeof item?.name === "string" &&
          isFiltered(search, item.name.toLowerCase(), searchTranslateLibrary)
        );
      });
    }
    return options;
  }, [search, options]);

  useEffect(() => {
    if (isDisabled) {
      onClickDisabled();
    } else if (trigger) {
      if (options.length > 1 && selected?.length === 0) {
        setIsOpen(true);
      }
    }
  }, [trigger]);

  const label = useMemo(() => {
    if (selected.length === 0) {
      return placeholder;
    }
    if (selected.length === 1) {
      return options.find((option: any) => option.value === selected[0])?.name;
    }
    return `Выбрано ${declOfNum(selected.length, ["элемент", "элемента", "элементов"])}`;
  }, [selected]);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      // @ts-ignore
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      // @ts-ignore
      inputRef.current.focus();
      onOpened();
    }
  }, [isOpen]);

  return (
    <div
      className={clsx(
        styles.Container,
        type === "small" && styles.ContainerSmall,
      )}
      ref={containerRef}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        className={clsx(
          isOpen && styles.SelectBoxOpened,
          !isOpen && selected.length > 0 && styles.SelectBoxSelected,
          styles.SelectBox,
        )}
        data-test={test ? `${test}Box` : undefined}
        onClick={() => {
          if (!isDisabled) {
            setIsOpen(!isOpen);
          } else {
            onClickDisabled();
          }
        }}
      >
        {!isOpen && selected.length > 0 && <span>{label}</span>}
        {!isOpen && selected.length === 0 && <span>{label}</span>}
        {isOpen && (
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.SearchInput}
          />
        )}

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="#9DA4AE"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div className={styles.Dropdown}>
          <div className={styles.OptionsList}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option: any) => (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                <div
                  key={option.value}
                  className={styles.Option}
                  data-test={test ? `${test}Option` : undefined}
                  data-value={option.value}
                >
                  <Checkbox
                    label={option.name}
                    count={option.count}
                    checked={selected.includes(option.value)}
                    onChange={() => {
                      setSearch("");
                      setIsOpen(false);
                      onChange(option.value);
                      // onChange(
                      //   selected.includes(option.value) ? "" : option.value,
                      // );
                    }}
                  />
                </div>
              ))
            ) : (
              <div className={styles.NoOptions}>Ничего не найдено</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
